import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { PageLayout, PageHeader, Button, StatusBadge, formatDate } from "../../ui";
import { useAuthContext } from "../auth/hooks";
import { useSuppressions } from "./hooks";
import { unsuppressEmail } from "./api";
import { SuppressedEmail } from "./types";

/**
 * Staff-only view of the SES suppression list (issue #1382). Addresses land here
 * automatically when SES reports a hard bounce or a spam complaint; alerting skips
 * them so we stop emailing dead/complained addresses. Staff can un-suppress once a
 * mailbox issue is resolved — a fresh bounce/complaint re-suppresses on its own.
 */
export function SuppressionsPage() {
  const { isStaff, isLoading } = useAuthContext();
  const { rows, loading, error, refetch, removeLocal } = useSuppressions(isStaff);
  const [pending, setPending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Staff-only. The backend re-checks (403 for non-staff), so this is a UX gate.
  if (!isLoading && !isStaff) {
    return <Navigate to="/insights" replace />;
  }

  const handleUnsuppress = async (email: string) => {
    if (
      !window.confirm(
        `Un-suppress ${email}? Alerting will be able to email this address ` +
          `again. A future bounce or complaint will re-suppress it automatically.`
      )
    ) {
      return;
    }
    setPending(email);
    setActionError(null);
    try {
      await unsuppressEmail(email);
      removeLocal(email);
    } catch (e: any) {
      setActionError(e?.message ?? `Failed to un-suppress ${email}.`);
    } finally {
      setPending(null);
    }
  };

  return (
    <PageLayout>
      <div className="mx-auto max-w-4xl p-8">
        <PageHeader
          title="Suppressed emails"
          badge={
            !loading && !error ? (
              <StatusBadge variant="neutral">
                {rows.length} {rows.length === 1 ? "address" : "addresses"}
              </StatusBadge>
            ) : undefined
          }
          description={
            <>
              Addresses SES reported as a hard bounce or spam complaint. Alerting
              never emails these, protecting sender reputation. Un-suppress an
              address once the mailbox issue is resolved — a fresh bounce or
              complaint re-suppresses it automatically.
            </>
          }
        />

        {actionError && (
          <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {actionError}
          </div>
        )}

        <div className="rounded-md border border-border">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">
              Loading suppression list…
            </div>
          ) : error ? (
            <div className="flex items-center justify-between gap-4 p-6 text-sm">
              <span className="text-destructive">{error}</span>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              No suppressed addresses. Hard bounces and complaints will appear here.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Reason</th>
                  <th className="px-4 py-3 font-medium">Detail</th>
                  <th className="px-4 py-3 font-medium">Suppressed</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <SuppressionRow
                    key={row.email}
                    row={row}
                    pending={pending === row.email}
                    onUnsuppress={() => handleUnsuppress(row.email)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PageLayout>
  );
}

function SuppressionRow({
  row,
  pending,
  onUnsuppress,
}: {
  row: SuppressedEmail;
  pending: boolean;
  onUnsuppress: () => void;
}) {
  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="px-4 py-3 font-medium text-foreground">{row.email}</td>
      <td className="px-4 py-3">
        <StatusBadge variant={row.reason === "complaint" ? "error" : "neutral"}>
          {row.reason}
        </StatusBadge>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{row.detail ?? "—"}</td>
      <td className="px-4 py-3 text-muted-foreground">
        {formatDate(row.suppressed_at)}
      </td>
      <td className="px-4 py-3 text-right">
        <Button
          variant="dangerOutline"
          size="sm"
          disabled={pending}
          onClick={onUnsuppress}
        >
          {pending ? "Un-suppressing…" : "Un-suppress"}
        </Button>
      </td>
    </tr>
  );
}
